import { byPrefixAndName } from '@awesome.me/kit-39de79544e/icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faBlockQuestion,
  faClapperboardPlay,
  faHeartRate,
  faHexagon,
  faPersonDolly,
  faSwords,
  faTachometerAltAverage,
  faUsers,
} from '@fortawesome/pro-light-svg-icons'
import { Box, Card, Skeleton, Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCampaign } from '@/api/campaigns/get'
import TooFast from '@/assets/images/too-fast.png'
import CardHeader from '@/common/components/Card/CardHeader'
import CardSummeryIcon, { CardSummeryIconLoading } from '@/common/components/Card/CardSummeryIcon'
import LoadingContainer from '@/common/components/LoadingContainer/LoadingContainer'
import Popup from '@/common/components/Popup'
import { AssetType, CampaignStatus } from '@/types/campaigns'
import { theme } from '@/theme/theme'

type CampaignSummaryProps = {
  campaignId: string
}

const CampaignSummary: FC<CampaignSummaryProps> = ({ campaignId }) => {
  const { t } = useTranslation()
  const [isReportAlertOpen, setReportAlertOpen] = useState(false)

  const { data: campaignData, isPending } = useCampaign(campaignId)

  const isPackage = useMemo(() => {
    return campaignData?.assets && campaignData?.assets?.length > 1
  }, [campaignData])

  const difficultyLevel = useMemo(() => {
    if (isPending || !campaignData) {
      return 0
    }
    if (campaignData.asset_type === AssetType.phishing_simulation) {
      const uniqueDifficulties = new Set(campaignData?.assets.flatMap((a) => a.difficulties))
      return Array.from(uniqueDifficulties)
        .sort((a, b) => a - b)
        .join(', ')
    } else {
      return 0
    }
  }, [campaignData])

  // This will be removed when new campaign api will be implemented
  const attackVecktor = useMemo(() => {
    if (isPending) {
      return ''
    }
    if (campaignData?.asset_type === AssetType.phishing_simulation) {
      const vectors = campaignData?.assets.flatMap((a) => a.vectors)
      if (vectors?.length > 1) {
        return t('common.campaign.mixed')
      } else {
        return t(`common.campaign.${vectors[0]}`)
      }
    } else {
      return ''
    }
  }, [campaignData])

  const campaigsStatus = useMemo(() => {
    if (isPending) {
      return ''
    }
    if ([CampaignStatus.pending, CampaignStatus.processing, CampaignStatus.scheduled].includes(campaignData?.status)) {
      return t(`common.campaign.${CampaignStatus.scheduled}`)
    } else {
      return t(`common.campaign.${campaignData?.status}`)
    }
  }, [campaignData])

  const campaignType = useMemo(() => {
    if (isPending || !campaignData) {
      return ''
    }
    if (isPackage) {
      if (campaignData.asset_type === AssetType.phishing_simulation) {
        return t(`campaignDashboard.campaignSummary.simulationPackageType`)
      }
      return t(`campaignDashboard.campaignSummary.videoPackageType`)
    } else if (campaignData.asset_type === AssetType.phishing_simulation) {
      return t(`common.campaign.phishing_simulation`)
    } else if (campaignData.asset_type === AssetType.training_video && campaignData?.asset_metadata?.include_quiz) {
      return t(`common.campaign.training_video_quiz`)
    } else if (campaignData.asset_type === AssetType.training_video) {
      return t(`common.campaign.training_video`)
    }
  }, [campaignData])

  if (!campaignData || isPending)
    return (
      <Card>
        <LoadingContainer />
      </Card>
    )

  return (
    <>
      {isReportAlertOpen && (
        <Popup
          open={isReportAlertOpen}
          onClose={() => setReportAlertOpen(false)}
          onConfirm={() => setReportAlertOpen(false)}
          buttonLabels={{
            confirm: t('trainingCampaignDashboard.reportAlert.acknowledge'),
          }}
          icon={TooFast}>
          <h4>{t('trainingCampaignDashboard.reportAlert.title')}</h4>
          <Typography>{t('trainingCampaignDashboard.reportAlert.message')}</Typography>
        </Popup>
      )}
      <Card>
        <CardHeader>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={theme.typography.fontWeightSemiBold}>
              {t('campaignDashboard.campaignSummary.title')}
            </Typography>{' '}
            -
            {isPending ? <Skeleton width={240} height={18} variant="rectangular" /> : <span>{campaignData?.name}</span>}
          </Box>
        </CardHeader>

        <Box display="flex" justifyContent="stretch" marginTop={2}>
          {isPending ? (
            Array(6)
              .fill('')
              .map((_, index) => <CardSummeryIconLoading key={index} />)
          ) : (
            <>
              {campaignData?.asset_type === AssetType.phishing_simulation ? (
                <>
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.campaignType')}
                    title={campaignType}
                    Icon={faHexagon}
                  />
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.difficultyLevel')}
                    title={difficultyLevel}
                    Icon={faTachometerAltAverage}
                  />
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.totalMembers')}
                    title={campaignData.total_members}
                    Icon={faUsers}
                  />
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.deliveredMessages')}
                    title={campaignData.delivered_messages}
                    Icon={faPersonDolly}
                  />
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.attackVecktor')}
                    title={attackVecktor}
                    Icon={faSwords}
                  />
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.status')}
                    title={campaigsStatus}
                    Icon={faHeartRate}
                  />
                </>
              ) : (
                <>
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.campaignType')}
                    title={campaignType}
                    Icon={faHexagon}
                  />

                  {isPackage ? (
                    <>
                      <CardSummeryIcon
                        subtitle={t('campaignDashboard.campaignSummary.totalVideos')}
                        title={campaignData?.assets?.length || 'N/A'}
                        Icon={faClapperboardPlay}
                      />
                      <CardSummeryIcon
                        subtitle={t('campaignDashboard.campaignSummary.totalQuizzes')}
                        title={
                          campaignData.asset_metadata.include_quiz
                            ? campaignData?.assets.filter((asset) => asset.number_of_quiz_questions).length
                            : 'N/A'
                        }
                        Icon={faBlockQuestion}
                      />
                    </>
                  ) : (
                    <CardSummeryIcon
                      subtitle={t('campaignDashboard.campaignSummary.totalQuestions')}
                      title={
                        campaignData.asset_metadata.include_quiz
                          ? campaignData?.assets[0]?.number_of_quiz_questions || 'N/A'
                          : 'N/A'
                      }
                      Icon={byPrefixAndName.fak['light-block-question-circle-check'] as IconProp}
                    />
                  )}
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.totalMembers')}
                    title={campaignData.total_members}
                    Icon={faUsers}
                  />
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.deliveredMessages')}
                    title={campaignData.delivered_messages}
                    Icon={faPersonDolly}
                  />
                  <CardSummeryIcon
                    subtitle={t('campaignDashboard.campaignSummary.status')}
                    title={campaigsStatus}
                    Icon={faHeartRate}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Card>
    </>
  )
}

export default CampaignSummary
